import type { HTMLAttributes, ReactNode } from 'react';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type Tracking = 'snug' | 'default' | 'wide' | 'wider' | 'widest';
type Tone = 'muted' | 'fg' | 'accent';

export interface MonoLabelProps extends HTMLAttributes<HTMLSpanElement> {
  size?: Size;
  tracking?: Tracking;
  tone?: Tone;
  as?: 'span' | 'div';
  children: ReactNode;
}

const toneClass: Record<Tone, string> = {
  muted: '',
  fg: 'pn-mono-fg',
  accent: 'pn-mono-accent'
};

export function MonoLabel({
  size = 'md',
  tracking = 'default',
  tone = 'muted',
  as = 'span',
  className,
  children,
  ...rest
}: MonoLabelProps) {
  const cls = ['pn-mono', `pn-mono-${size}`, `pn-mono-t-${tracking}`, toneClass[tone], className]
    .filter(Boolean)
    .join(' ');
  const Tag = as;
  return (
    <Tag className={cls} {...rest}>
      {children}
    </Tag>
  );
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={['pn-eyebrow', className].filter(Boolean).join(' ')}>
      <span className="pn-eyebrow-sq" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  accent?: boolean;
  /** Render a trailing × button that fires `onDismiss` (mouse + Enter/Space). */
  dismissible?: boolean;
  /** Fired when the × button is clicked (or Enter/Space pressed on it). */
  onDismiss?: () => void;
  /** ARIA label for the × button (defaults to "Remove"). */
  dismissLabel?: string;
  disabled?: boolean;
  children: ReactNode;
}

export function Tag({
  accent,
  dismissible,
  onDismiss,
  dismissLabel = 'Remove',
  disabled,
  children,
  className,
  ...rest
}: TagProps) {
  return (
    <span
      className={[
        'pn-tag',
        accent ? 'pn-tag-accent' : '',
        dismissible ? 'pn-tag-dismissible' : '',
        className
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <span className="pn-tag-label">{children}</span>
      {dismissible ? (
        <button
          type="button"
          className="pn-tag-dismiss"
          aria-label={dismissLabel}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss?.();
          }}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}
